import React, { useEffect, useState } from 'react'
import { Breadcrumb, Card, Container } from 'react-bootstrap'
import DatasourceForm from '../../components/forms/DatasourceForm'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Datasource, ObjectStorageDatasource, RemoteDatasource } from '../../models/Datasource';
import { Datasources } from '../../api/Datasources';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import AlidaLoader from '../../components/common/AlidaLoader';
import { Helmet } from 'react-helmet';
import { Alert } from 'antd';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import { setObjectType } from '../../utils';

export default function DatasourceEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [datasource, setDatasource] = useState<Datasource>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  useEffect(() => {
    if (id) initDatasource();
    else setDatasource(new Datasource());
    // eslint-disable-next-line
  }, []);

  const initDatasource = () => {
    Datasources.getDatasource(+id!)
      .then(
        ({ data: ds }) => setDatasource(ds),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }

  const onCancel = () => navigate("/datasources");

  const onSubmit = (datasource: Datasource) => {
    showLoader();
    const body = { ...datasource };
    if (body.type === 'objectStorage' && setObjectType<ObjectStorageDatasource>(body)) body.secure = body.secure || false;
    else if (body.type === 'remote' && setObjectType<RemoteDatasource>(body)) body.auth = typeof body.auth === 'string' ? JSON.parse(body.auth) : body.auth;
    const promise = id
      ? Datasources.updateDatasource(body)
      : Datasources.createDatasource(body);

    promise.then(
      ({ data: newDatasource }) => {
        hideLoader();
        showToast('submit-datasource-success', `${assetLabels.singular} "${newDatasource.name}" ${id ? "updated" : "created"} successfully`, `${assetLabels.singular} ${id ? "Updated" : "Created"}`, 'success');
        navigate(`/datasources/${newDatasource.id}`);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('submit-datasource-error', message || `Could not save ${assetLabels.singular}`, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        hideLoader();
      }
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert
        showIcon
        type="error"
        message={`An error occurred during ${assetLabels.singular} retrieve. Try again later`}
      />
    )

    return datasource
      ? <DatasourceForm datasource={datasource} onSubmit={onSubmit} onCancel={onCancel} />
      : (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={300} />
        </div>
      );
  }

  return (
    <Container className="my-3">
      <Helmet>
        <title>{assetLabels.singular} {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasources' }}>{assetLabels.plural}</Breadcrumb.Item>
        <Breadcrumb.Item active>{id ? (datasource?.name || assetLabels.singular) + ' Edit' : `New ${assetLabels.singular}`}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Card.Header className="bg-white">
          <h5>{id ? (datasource?.name || assetLabels.singular) + " Edit" : `New ${assetLabels.singular}`}</h5>
          <h6 className="mb-0 text-muted">Fill in {id ? "existing" : "new"} {assetLabels.singular} details</h6>
        </Card.Header>
        <Card.Body>
          {renderAsyncContent()}
        </Card.Body>
      </Card>
    </Container>
  )
}
